import React, { useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Progress, Alert } from 'reactstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { BASE_URL } from '../constants/apiConfig';

const ExportModal = ({ isOpen, toggle, selectedApplications, applications, onExportComplete }) => {
	const [exporting, setExporting] = useState(false);
	const [progress, setProgress] = useState(0);
	const [currentApp, setCurrentApp] = useState('');

	const handleExport = async () => {
		setExporting(true);
		setProgress(0);

		try {
			const token = sessionStorage.getItem('token');

			// Request batch export
			const response = await axios.post(
				`${BASE_URL}/api/admin/dashboard/applications/batch-export`,
				{
					applicationIds: selectedApplications,
				},
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
					responseType: 'blob',
					onDownloadProgress: (progressEvent) => {
						const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
						setProgress(percentCompleted);
					},
				}
			);

			// Create blob link to download
			const url = window.URL.createObjectURL(new Blob([response.data]));
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', `applications_export_${new Date().getTime()}.zip`);
			document.body.appendChild(link);
			link.click();
			link.parentNode.removeChild(link);

			toast.success(`Successfully exported ${selectedApplications.length} application(s)`);
			onExportComplete();
			toggle();
		} catch (error) {
			console.error('Export error:', error);
			toast.error('Failed to export applications. Please try again.');
		} finally {
			setExporting(false);
			setProgress(0);
			setCurrentApp('');
		}
	};

	const selectedApps = applications.filter((app) => selectedApplications.includes(app.id));

	return (
		<Modal isOpen={isOpen} toggle={toggle} size="md">
			<ModalHeader toggle={toggle}>
				<span>📦</span> Export Applications
			</ModalHeader>
			<ModalBody>
				{exporting ? (
					<div>
						<p>Exporting applications... Please wait.</p>
						{currentApp && <p className="text-muted small">Processing: {currentApp}</p>}
						<Progress value={progress} className="mb-3">
							{progress}%
						</Progress>
						<Alert color="info" className="small">
							<strong>Note:</strong> Each application will be exported as a separate folder containing all
							documents (proposal, writing sample, resume, certificates, recommendations, and annexures).
						</Alert>
					</div>
				) : (
					<div>
						<p>
							You are about to export <strong>{selectedApplications.length}</strong> application(s).
						</p>

						<div className="export-preview mb-3" style={{ maxHeight: '300px', overflowY: 'auto' }}>
							<ul className="list-unstyled">
								{selectedApps.slice(0, 10).map((app) => (
									<li key={app.id} className="mb-2">
										<span className="text-primary">•</span> {app.name} ({app.email})
									</li>
								))}
								{selectedApps.length > 10 && (
									<li className="text-muted">... and {selectedApps.length - 10} more</li>
								)}
							</ul>
						</div>

						<Alert color="warning" className="small">
							<strong>Export Contents:</strong>
							<ul className="mb-0 mt-2">
								<li>CV/Resume</li>
								<li>Book Proposal</li>
								<li>Writing Sample</li>
								<li>Educational Certificates</li>
								<li>Recommendation Letters (both)</li>
								<li>Additional Documents/Annexures</li>
							</ul>
						</Alert>

						<Alert color="info" className="small">
							<strong>Format:</strong> All files will be organized in a single ZIP file with separate folders
							for each applicant.
						</Alert>
					</div>
				)}
			</ModalBody>
			<ModalFooter>
				<Button color="secondary" onClick={toggle} disabled={exporting}>
					Cancel
				</Button>
				<Button color="success" onClick={handleExport} disabled={exporting}>
					{exporting ? 'Exporting...' : 'Export ZIP'}
				</Button>
			</ModalFooter>
		</Modal>
	);
};

export default ExportModal;
